import { useState, useEffect } from 'react';
import { getAllAdminActions, rollbackAdminAction } from '../../services/adminActions';
import { useToast } from '../../hooks/useToast';
import type { AdminAction } from '../../types/user';

type FilterType = 'all' | 'user' | 'lesson' | 'staff_invitation';

const TARGET_TYPE_LABEL: Record<string, string> = {
  user: 'User',
  lesson: 'Lesson',
  staff_invitation: 'Staff Invitation',
};

const ACTION_COLORS: Record<string, string> = {
  user: 'bg-blue-100 text-blue-700',
  lesson: 'bg-green-100 text-green-700',
  staff_invitation: 'bg-purple-100 text-purple-700',
  rollback: 'bg-gray-100 text-gray-600',
};

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'boolean') return val ? 'true' : 'false';
  return String(val);
}

function DeltaList({ changes }: { changes: Record<string, { from: unknown; to: unknown }> }) {
  const entries = Object.entries(changes);
  if (entries.length === 0) return null;
  return (
    <div className="mt-1 space-y-0.5">
      {entries.map(([field, delta]) => (
        <div key={field} className="text-[11px] text-[#6B7280] font-mono">
          <span className="text-[#9CA3AF]">{field}:</span>{' '}
          <span className="line-through text-red-400">{formatValue(delta.from)}</span>
          {' → '}
          <span className="text-green-600">{formatValue(delta.to)}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminActionLog() {
  const [actions, setActions] = useState<AdminAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [rolling, setRolling] = useState<string | null>(null);
  const { showSuccess, showError } = useToast();

  async function load() {
    setLoading(true);
    try {
      setActions(await getAllAdminActions());
    } catch {
      showError('Failed to load action log');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleRollback(action: AdminAction) {
    if (!confirm(`Roll back "${action.actionLabel}" on ${action.targetLabel || action.targetId}?\n\nThis will restore the previous field values and log a new inverse action.`)) return;
    setRolling(action.id);
    try {
      await rollbackAdminAction(action.id);
      showSuccess('Action rolled back');
      load();
    } catch (err: unknown) {
      const msg = (err as { message?: string }).message || 'Failed to rollback action';
      showError(msg);
    } finally {
      setRolling(null);
    }
  }

  const filtered = filter === 'all'
    ? actions
    : actions.filter(a => a.targetType === filter || (filter === 'user' && a.action === 'rollback' && a.targetType === 'user'));

  const filterCounts: Record<FilterType, number> = {
    all: actions.length,
    user: actions.filter(a => a.targetType === 'user').length,
    lesson: actions.filter(a => a.targetType === 'lesson').length,
    staff_invitation: actions.filter(a => a.targetType === 'staff_invitation').length,
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Action Log</h1>
        <p className="text-gray-600">Full audit trail of admin actions. Roll back any entry to restore previous field values.</p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-2 flex-wrap">
        {(['all', 'user', 'lesson', 'staff_invitation'] as FilterType[]).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg font-medium transition text-sm ${
              filter === f
                ? 'bg-[#1A6B8A] text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {f === 'all' ? 'All' : TARGET_TYPE_LABEL[f] ?? f} ({filterCounts[f]})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          No actions recorded yet
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actor</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Changes</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Rollback</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(action => {
                  const isRolledBack = !!action.rolledBackAt;
                  const colorKey = action.action === 'rollback' ? 'rollback' : action.targetType;
                  return (
                    <tr key={action.id} className={`hover:bg-gray-50 ${isRolledBack ? 'opacity-50' : ''}`}>
                      <td className="px-5 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {new Date(action.createdAt).toLocaleString()}
                      </td>
                      <td className="px-5 py-3">
                        <div className="text-sm font-medium text-gray-900">{action.actorName}</div>
                        <div className="text-xs text-gray-400 capitalize">{action.actorRole}</div>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${ACTION_COLORS[colorKey] ?? 'bg-gray-100 text-gray-600'}`}>
                          {action.actionLabel}
                        </span>
                        {isRolledBack && (
                          <div className="mt-1 text-xs text-gray-400">
                            Rolled back by {action.rolledBackByName} · {action.rolledBackAt ? new Date(action.rolledBackAt).toLocaleString() : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <div className="text-sm text-gray-900">{action.targetLabel || action.targetId}</div>
                        <div className="text-xs text-gray-400">{TARGET_TYPE_LABEL[action.targetType] ?? action.targetType} · {action.targetId.slice(0, 8)}…</div>
                      </td>
                      <td className="px-5 py-3 max-w-xs">
                        <DeltaList changes={action.changes ?? {}} />
                      </td>
                      <td className="px-5 py-3 text-right">
                        {action.rollbackable && !isRolledBack ? (
                          <button
                            onClick={() => handleRollback(action)}
                            disabled={rolling === action.id}
                            className="px-3 py-1.5 text-xs font-semibold text-[#1A6B8A] border border-[#E4E4E2] rounded-lg hover:bg-[#EFF6FF] transition-colors disabled:opacity-50"
                          >
                            {rolling === action.id ? 'Rolling back…' : 'Rollback'}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
